import React, { useEffect, useMemo, useState } from "react";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Banknote, RefreshCw, PlusCircle, MinusCircle, LockKeyhole, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const DENOMINATIONS = [50000, 20000, 10000, 5000, 2000, 1000, 500, 100, 50, 25, 10, 5];

const n = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString("es-CR");
};
const diffClass = (value) => Math.abs(n(value)) < 0.01 ? "text-emerald-700" : "text-destructive";

export default function CashRegisterPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);

  const [openForm, setOpenForm] = useState({
    register_id: "CABADI-01",
    register_name: "Caja principal",
    location: "LabAroid",
    opening_float: "0",
    opening_note: "",
  });

  const [movement, setMovement] = useState({ type: "OUT", amount: "", reason: "", reference: "" });
  const [denominations, setDenominations] = useState(() => Object.fromEntries(DENOMINATIONS.map((d) => [String(d), 0])));
  const [closeForm, setCloseForm] = useState({
    declared_sinpe: "",
    declared_card: "",
    declared_transfer: "",
    declared_other: "",
    discrepancy_reason: "",
    closing_note: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [statusRes, historyRes] = await Promise.all([
        api.post("/gsheet/cash/status", {}),
        api.post("/gsheet/cash/history", {}),
      ]);
      setStatus(statusRes.data || null);
      setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cargar el módulo de caja.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const countedCash = useMemo(() => DENOMINATIONS.reduce((sum, denomination) => {
    return sum + denomination * Math.max(Math.floor(n(denominations[String(denomination)])), 0);
  }, 0), [denominations]);

  const summary = status?.summary || null;
  const session = status?.session || null;
  const expectedCash = n(summary?.expectedCash);
  const cashDifference = countedCash - expectedCash;
  const reconciliation = {
    sinpe: n(closeForm.declared_sinpe) - n(summary?.payments?.sinpe),
    card: n(closeForm.declared_card) - n(summary?.payments?.card),
    transfer: n(closeForm.declared_transfer) - n(summary?.payments?.transfer),
    other: n(closeForm.declared_other) - n(summary?.payments?.other),
  };
  const hasDifference = [cashDifference, ...Object.values(reconciliation)].some((v) => Math.abs(v) > 0.009);

  const openSession = async () => {
    if (n(openForm.opening_float) < 0) return toast.error("El fondo inicial no puede ser negativo.");
    setSaving(true);
    try {
      const { data } = await api.post("/gsheet/cash/open", {
        ...openForm,
        opening_float: n(openForm.opening_float),
      });
      setStatus(data);
      setOpenForm((f) => ({ ...f, opening_float: "0", opening_note: "" }));
      toast.success("Caja abierta correctamente.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo abrir la caja.");
    } finally {
      setSaving(false);
    }
  };

  const registerMovement = async () => {
    if (!session?.cash_session_id) return;
    if (n(movement.amount) <= 0) return toast.error("El monto debe ser mayor que cero.");
    if (!movement.reason.trim()) return toast.error("El motivo del movimiento es obligatorio.");
    setSaving(true);
    try {
      const { data } = await api.post("/gsheet/cash/movement", {
        session_id: String(session.cash_session_id),
        movement_type: movement.type,
        amount: n(movement.amount),
        reason: movement.reason.trim(),
        reference: movement.reference.trim(),
      });
      setStatus(data);
      setMovement({ type: "OUT", amount: "", reason: "", reference: "" });
      toast.success("Movimiento de caja registrado.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo registrar el movimiento.");
    } finally {
      setSaving(false);
    }
  };

  const closeSession = async () => {
    if (!session?.cash_session_id) return;
    if (hasDifference && !closeForm.discrepancy_reason.trim()) {
      return toast.error("Existe una diferencia. Debes documentar su motivo antes de cerrar.");
    }
    setSaving(true);
    try {
      await api.post("/gsheet/cash/close", {
        session_id: String(session.cash_session_id),
        denominations,
        declared_sinpe: n(closeForm.declared_sinpe),
        declared_card: n(closeForm.declared_card),
        declared_transfer: n(closeForm.declared_transfer),
        declared_other: n(closeForm.declared_other),
        discrepancy_reason: closeForm.discrepancy_reason.trim(),
        closing_note: closeForm.closing_note.trim(),
      });
      setDenominations(Object.fromEntries(DENOMINATIONS.map((d) => [String(d), 0])));
      setCloseForm({ declared_sinpe: "", declared_card: "", declared_transfer: "", declared_other: "", discrepancy_reason: "", closing_note: "" });
      toast.success("Caja cerrada y arqueo registrado.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cerrar la caja.");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !status) {
    return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin" /></div>;
  }

  return (
    <main className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6" data-testid="cash-register-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2"><Banknote className="h-7 w-7 text-primary" /> Caja y arqueo</h1>
          <p className="text-sm text-muted-foreground mt-1">Apertura, control, conciliación y cierre auditable. Fuente operativa: Google Sheets.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading || saving} className="gap-2"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Lectura de control (X)</Button>
      </header>

      {!status?.active ? (
        <section className="bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-sm max-w-3xl">
          <h2 className="font-display text-xl font-bold">Abrir caja</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-5">Registra el fondo real con el que inicia el turno. Este valor forma parte del arqueo final.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="ID de caja"><Input value={openForm.register_id} onChange={(e) => setOpenForm({ ...openForm, register_id: e.target.value })} /></Field>
            <Field label="Nombre de caja"><Input value={openForm.register_name} onChange={(e) => setOpenForm({ ...openForm, register_name: e.target.value })} /></Field>
            <Field label="Ubicación"><Input value={openForm.location} onChange={(e) => setOpenForm({ ...openForm, location: e.target.value })} /></Field>
            <Field label="Fondo inicial (CRC)"><Input type="number" min="0" step="5" value={openForm.opening_float} onChange={(e) => setOpenForm({ ...openForm, opening_float: e.target.value })} /></Field>
            <div className="sm:col-span-2"><Field label="Nota de apertura"><Input placeholder="Ej. fondo dejado del cierre anterior" value={openForm.opening_note} onChange={(e) => setOpenForm({ ...openForm, opening_note: e.target.value })} /></Field></div>
          </div>
          <Button className="mt-5" onClick={openSession} disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Abrir turno de caja</Button>
        </section>
      ) : (
        <>
          <section className="bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2"><span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" /><h2 className="font-display text-xl font-bold">Caja abierta · {session?.cash_session_id}</h2></div>
                <p className="text-sm text-muted-foreground mt-1">{session?.register_name} · {session?.register_id} · {session?.location}</p>
                <p className="text-xs text-muted-foreground mt-1">Abierta {fmtDate(session?.opened_at)} por {session?.opened_by || "—"}</p>
              </div>
              <div className="text-left lg:text-right"><p className="text-xs text-muted-foreground">Fondo inicial</p><p className="font-mono text-xl font-bold">{money(summary?.openingFloat)}</p></div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6">
              <Metric label="Ventas netas creadas" value={summary?.orders?.netSalesTotal} sub={`${summary?.orders?.createdCount || 0} órdenes`} />
              <Metric label="Cobros del turno" value={summary?.payments?.total} />
              <Metric label="Efectivo cobrado" value={summary?.payments?.cash} />
              <Metric label="SINPE cobrado" value={summary?.payments?.sinpe} />
              <Metric label="Efectivo esperado" value={summary?.expectedCash} emphasize />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <MiniMetric label="Tarjeta" value={summary?.payments?.card} />
              <MiniMetric label="Transferencia" value={summary?.payments?.transfer} />
              <MiniMetric label="Otros" value={summary?.payments?.other} />
              <MiniMetric label="Canceladas" value={summary?.orders?.cancelledSalesTotal} sub={`${summary?.orders?.cancelledCount || 0} órdenes`} />
            </div>
          </section>

          <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-sm">
              <h2 className="font-display text-lg font-bold">Movimientos físicos de efectivo</h2>
              <p className="text-sm text-muted-foreground mt-1">Solo entradas o salidas de la gaveta que no sean cobros normales de venta.</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setMovement({ ...movement, type: "IN" })} className={`flex-1 rounded-xl border p-3 text-sm font-semibold flex items-center justify-center gap-2 ${movement.type === "IN" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-border"}`}><PlusCircle className="h-4 w-4" /> Entrada</button>
                <button onClick={() => setMovement({ ...movement, type: "OUT" })} className={`flex-1 rounded-xl border p-3 text-sm font-semibold flex items-center justify-center gap-2 ${movement.type === "OUT" ? "bg-amber-50 border-amber-300 text-amber-800" : "border-border"}`}><MinusCircle className="h-4 w-4" /> Salida</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <Field label="Monto (CRC)"><Input type="number" min="0" step="5" value={movement.amount} onChange={(e) => setMovement({ ...movement, amount: e.target.value })} /></Field>
                <Field label="Referencia"><Input placeholder="Opcional" value={movement.reference} onChange={(e) => setMovement({ ...movement, reference: e.target.value })} /></Field>
                <div className="sm:col-span-2"><Field label="Motivo"><Input placeholder="Obligatorio: depósito, gasto menor, fondo adicional..." value={movement.reason} onChange={(e) => setMovement({ ...movement, reason: e.target.value })} /></Field></div>
              </div>
              <div className="flex justify-between text-sm mt-4 border-t border-border pt-4"><span>Entradas registradas</span><strong>{money(summary?.movements?.cashIn)}</strong></div>
              <div className="flex justify-between text-sm mt-1"><span>Salidas registradas</span><strong>{money(summary?.movements?.cashOut)}</strong></div>
              <Button variant="outline" className="mt-4" onClick={registerMovement} disabled={saving}>Registrar movimiento</Button>
            </div>

            <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-sm">
              <h2 className="font-display text-lg font-bold">Conciliación de medios no efectivos</h2>
              <p className="text-sm text-muted-foreground mt-1">Ingresa lo verificado en el reporte externo de cada medio. No se prellena para evitar una conciliación ficticia.</p>
              <div className="space-y-3 mt-4">
                <ReconcileRow label="SINPE" expected={summary?.payments?.sinpe} value={closeForm.declared_sinpe} onChange={(v) => setCloseForm({ ...closeForm, declared_sinpe: v })} difference={reconciliation.sinpe} />
                <ReconcileRow label="Tarjeta" expected={summary?.payments?.card} value={closeForm.declared_card} onChange={(v) => setCloseForm({ ...closeForm, declared_card: v })} difference={reconciliation.card} />
                <ReconcileRow label="Transferencia" expected={summary?.payments?.transfer} value={closeForm.declared_transfer} onChange={(v) => setCloseForm({ ...closeForm, declared_transfer: v })} difference={reconciliation.transfer} />
                <ReconcileRow label="Otros" expected={summary?.payments?.other} value={closeForm.declared_other} onChange={(v) => setCloseForm({ ...closeForm, declared_other: v })} difference={reconciliation.other} />
              </div>
            </div>
          </section>

          <section className="bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-sm">
            <div className="flex items-start gap-3"><LockKeyhole className="h-5 w-5 text-primary mt-0.5" /><div><h2 className="font-display text-lg font-bold">Cierre de caja y arqueo (Z)</h2><p className="text-sm text-muted-foreground mt-1">Cuenta el efectivo físico. El servidor recalculará el total a partir de las denominaciones antes de guardar el cierre.</p></div></div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-5">
              {DENOMINATIONS.map((d) => (
                <label key={d} className="rounded-xl border border-border p-3 bg-secondary/30">
                  <span className="text-xs text-muted-foreground">₡{d.toLocaleString("es-CR")}</span>
                  <Input type="number" min="0" step="1" value={denominations[String(d)]} onChange={(e) => setDenominations({ ...denominations, [String(d)]: Math.max(Math.floor(n(e.target.value)), 0) })} className="mt-1" />
                  <span className="block text-xs font-mono mt-1">{money(d * n(denominations[String(d)]))}</span>
                </label>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
              <Metric label="Efectivo esperado" value={expectedCash} />
              <Metric label="Efectivo contado" value={countedCash} />
              <div className="rounded-xl border border-border p-4"><p className="text-xs text-muted-foreground">Diferencia efectivo</p><p className={`font-mono text-xl font-bold mt-1 ${diffClass(cashDifference)}`}>{money(cashDifference)}</p></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              <Field label={`Justificación de diferencias ${hasDifference ? "(obligatoria)" : "(si aplica)"}`}><Input value={closeForm.discrepancy_reason} onChange={(e) => setCloseForm({ ...closeForm, discrepancy_reason: e.target.value })} placeholder="Ej. faltante verificado, redondeo en efectivo, comprobante SINPE pendiente..." /></Field>
              <Field label="Nota de cierre"><Input value={closeForm.closing_note} onChange={(e) => setCloseForm({ ...closeForm, closing_note: e.target.value })} placeholder="Observación general del turno" /></Field>
            </div>

            <div className={`mt-5 rounded-xl border p-4 flex items-start gap-3 ${hasDifference ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`}>
              {hasDifference ? <AlertTriangle className="h-5 w-5 shrink-0" /> : <CheckCircle2 className="h-5 w-5 shrink-0" />}
              <div><p className="font-semibold">{hasDifference ? "El cierre presenta diferencias" : "Los valores ingresados cuadran con el sistema"}</p><p className="text-sm mt-0.5">{hasDifference ? "La diferencia se conservará en el historial. Debes documentar el motivo para cerrar." : "Al confirmar, la sesión quedará cerrada y registrada en Sheets."}</p></div>
            </div>

            <Button className="mt-5" onClick={closeSession} disabled={saving || (hasDifference && !closeForm.discrepancy_reason.trim())}>{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Confirmar cierre de caja</Button>
          </section>
        </>
      )}

      <section className="bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-sm">
        <h2 className="font-display text-lg font-bold">Historial de cierres</h2>
        <p className="text-sm text-muted-foreground mt-1">Últimas sesiones registradas en CASH_SESSIONS.</p>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm min-w-[850px]">
            <thead className="text-left text-muted-foreground border-b border-border"><tr><th className="py-2 pr-4">Sesión</th><th className="pr-4">Apertura</th><th className="pr-4">Cierre</th><th className="pr-4">Estado</th><th className="pr-4">Ventas netas</th><th className="pr-4">Cobros</th><th className="pr-4">Dif. efectivo</th><th>Responsable cierre</th></tr></thead>
            <tbody>{history.length ? history.map((h) => <tr key={h.cash_session_id} className="border-b border-border/60"><td className="py-3 pr-4 font-mono">{h.cash_session_id}</td><td className="pr-4">{fmtDate(h.opened_at)}</td><td className="pr-4">{fmtDate(h.closed_at)}</td><td className="pr-4">{h.status}</td><td className="pr-4 font-mono">{money(h.net_sales_total)}</td><td className="pr-4 font-mono">{money(h.payments_total)}</td><td className={`pr-4 font-mono ${diffClass(h.cash_difference)}`}>{money(h.cash_difference)}</td><td>{h.closed_by || "—"}</td></tr>) : <tr><td colSpan="8" className="py-6 text-center text-muted-foreground">Aún no hay sesiones de caja registradas.</td></tr>}</tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

const Field = ({ label, children }) => <label className="block"><span className="block text-xs font-semibold text-foreground mb-1.5">{label}</span>{children}</label>;
const Metric = ({ label, value, sub, emphasize = false }) => <div className={`rounded-xl border p-4 ${emphasize ? "border-primary/40 bg-primary/5" : "border-border"}`}><p className="text-xs text-muted-foreground">{label}</p><p className="font-mono text-lg sm:text-xl font-bold mt-1">{money(value)}</p>{sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}</div>;
const MiniMetric = ({ label, value, sub }) => <div className="rounded-xl bg-secondary/40 p-3"><p className="text-xs text-muted-foreground">{label}</p><p className="font-mono font-semibold mt-1">{money(value)}</p>{sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}</div>;
const ReconcileRow = ({ label, expected, value, onChange, difference }) => <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px_150px] gap-2 items-end rounded-xl border border-border p-3"><div><p className="font-semibold text-sm">{label}</p><p className="text-xs text-muted-foreground">Sistema: {money(expected)}</p></div><Field label="Verificado"><Input type="number" min="0" step="1" placeholder="0" value={value} onChange={(e) => onChange(e.target.value)} /></Field><div><p className="text-xs text-muted-foreground">Diferencia</p><p className={`font-mono font-semibold mt-2 ${diffClass(difference)}`}>{money(difference)}</p></div></div>;
